import { useState, useRef, useEffect } from "react";
import { ArrowRight, X, ChevronLeft, ChevronRight, Zap, DollarSign, Target, CheckCircle2, BookOpen, Workflow, Map } from "lucide-react";

// Playbook data with real images from Insights articles
const playbooks = [
  { 
    id: "account-mapping",
    title: "Account Mapping Playbook", 
    subtitle: "Interactive Workflow Builder",
    desc: "Run the account mapping workflow—from research and signals to buyer mapping and product value.",
    href: "/account-mapping-playbook/index.html",
    icon: Map,
    isFeatured: true,
    isExternal: true,
    image: "https://customer-assets.emergentagent.com/job_efficiency-boost-10/artifacts/hbdoli1e_ChatGPT%20Image%20May%2023%2C%202026%2C%2003_47_17%20PM.png",
    modalImage: "https://customer-assets.emergentagent.com/job_efficiency-boost-10/artifacts/hbdoli1e_ChatGPT%20Image%20May%2023%2C%202026%2C%2003_47_17%20PM.png",
    takeaways: [
      "5 connected layers from context to value",
      "25+ buyer signals translated into hypotheses",
      "11 copy-ready prompts for account analysis"
    ],
    preview: {
      chapters: ["Diagnostic", "Framework", "ICP", "Signals", "Prompts", "Budget"],
      stats: { pages: "Interactive", readTime: "15 min", frameworks: "5" }
    }
  },
  { 
    id: "ai-sdr-playbook",
    title: "AI SDR Training Playbook", 
    subtitle: "Build AI SDR Systems That Perform",
    desc: "Design, train, and operate AI SDR systems across the entire outbound funnel.",
    href: "/resources/ai-sdr-playbook",
    icon: Workflow,
    // Using the AI SDR Training Playbook article image
    image: "https://media.licdn.com/dms/image/v2/D5612AQHCPjDgc_XE_A/article-cover_image-shrink_720_1280/B56Z1ht6VsJ0AI-/0/1775460924383?e=2147483647&v=beta&t=3fFKvz5y3kul8snZrWf8WppC7xzxN16T3rskVZaXrWk",
    modalImage: "https://media.licdn.com/dms/image/v2/D5612AQHCPjDgc_XE_A/article-cover_image-shrink_720_1280/B56Z1ht6VsJ0AI-/0/1775460924383?e=2147483647&v=beta&t=3fFKvz5y3kul8snZrWf8WppC7xzxN16T3rskVZaXrWk",
    takeaways: [
      "6 core AI SDR workflows with operator checklists",
      "Failure modes and pro tips for each workflow",
      "Implementation roadmap from foundation to scale"
    ],
    preview: {
      chapters: ["Prospecting & Intent", "Account Research", "Content & Messaging", "Outreach Execution", "Follow-Up & Engagement", "Connected System"],
      stats: { pages: "24", readTime: "25 min", frameworks: "6" }
    }
  },
  { 
    id: "ai-sdr",
    title: "AI SDR Guidebook", 
    subtitle: "Build AI-Powered Outbound Engines",
    desc: "Build outbound engines that generate pipeline without burning out your team.",
    href: "/resources/ai-sdr-guidebook",
    icon: Zap,
    // Using the AI SDR article images
    image: "https://media.licdn.com/dms/image/v2/D5612AQHoqD9j6OPvfQ/article-cover_image-shrink_720_1280/B56ZuaiYovKEAI-/0/1767824267682?e=2147483647&v=beta&t=ruFDrcPhUkjhXWmtJ0Syx8_TeZX8JTAYko63S-061Lw",
    modalImage: "https://media.licdn.com/dms/image/v2/D5612AQHCPjDgc_XE_A/article-cover_image-shrink_720_1280/B56Z1ht6VsJ0AI-/0/1775460924383?e=2147483647&v=beta&t=3fFKvz5y3kul8snZrWf8WppC7xzxN16T3rskVZaXrWk",
    takeaways: [
      "Design AI workflows that complement human SDRs",
      "Scale outreach without sacrificing quality",
      "Measure AI impact on pipeline generation"
    ],
    preview: {
      chapters: ["AI Strategy Framework", "Workflow Design", "Tech Stack Selection", "Implementation Playbook", "Measurement & Optimization"],
      stats: { pages: "12", readTime: "15 min", frameworks: "4" }
    }
  },
  { 
    id: "sdr-economics",
    title: "SDR Economics", 
    subtitle: "The Complete Unit Economics Guide",
    desc: "Measure SDR investment by revenue impact, not activity volume.",
    href: "/resources/sdr-economics",
    icon: DollarSign,
    // Using the SDR Economics article image
    image: "https://media.licdn.com/dms/image/v2/D4D12AQGGRGuMGAL3mA/article-cover_image-shrink_720_1280/B4DZxTkMADHkAI-/0/1770928527443?e=2147483647&v=beta&t=-2FnyaulHj-TRfafbL3WELljIJJ6RvXNlkv14hi9tLM",
    modalImage: "https://media.licdn.com/dms/image/v2/D4E12AQFERGpcDp92eQ/article-cover_image-shrink_720_1280/B4EZwqUNXlGcAI-/0/1770236471162?e=2147483647&v=beta&t=lfcy6r_wTvttX1FS-k9lTRuXYLSoZijh-tkWi-lOyno",
    takeaways: [
      "Calculate true fully-loaded SDR cost",
      "Model payback periods and LTV impact",
      "Benchmark performance against best-in-class"
    ],
    preview: {
      chapters: ["Cost Analysis", "Payback Modeling", "Revenue Attribution", "Interactive Calculator", "Benchmark Data"],
      stats: { pages: "18", readTime: "20 min", frameworks: "3" }
    }
  },
  { 
    id: "talent-pool",
    title: "Talent System Guide", 
    subtitle: "Build Your AE Farm System",
    desc: "Develop SDRs who convert into top-performing AEs.",
    href: "/resources/talent-pool",
    icon: Target,
    // Using the Talent System article images
    image: "https://media.licdn.com/dms/image/v2/D5612AQGWYSSnqjD04Q/article-cover_image-shrink_720_1280/B56Zza7McgH8AI-/0/1773199482538?e=2147483647&v=beta&t=EdThB6PbZGoiEiUeYzUNigz3957oIopIj_ofVkBKV7k",
    modalImage: "https://media.licdn.com/dms/image/v2/D5612AQEOUEr3u3twSg/article-cover_image-shrink_720_1280/B56Z0A6bYwJ8AI-/0/1773836815134?e=2147483647&v=beta&t=eXM_aLqjof7nY6t01H10xJ81Fm_5KPRHxvd2exMBLL0",
    takeaways: [
      "Design career progression frameworks",
      "Identify high-potential SDRs early",
      "Build internal promotion pipelines"
    ],
    preview: {
      chapters: ["Hiring Framework", "Onboarding Systems", "Coaching Playbook", "Promotion Criteria", "Retention Strategy"],
      stats: { pages: "14", readTime: "18 min", frameworks: "5" }
    }
  }
];

// Playbook Card for Carousel/Grid - Image-driven
const PlaybookCard = ({ playbook, onPreview, isDesktop = false }) => {
  const Icon = playbook.icon;
  
  return (
    <div 
      className={`${isDesktop ? '' : 'flex-shrink-0 w-[85vw] sm:w-[340px] snap-center'}`}
      onClick={() => onPreview(playbook)}
    >
      <div className={`${isDesktop ? '' : 'mx-2'} bg-[#1C1C1C] rounded-2xl border ${playbook.isFeatured ? 'border-[#D4AF37]/40' : 'border-white/10'} overflow-hidden hover:border-[#D4AF37]/40 transition-all group cursor-pointer`}>
        {/* Image with Overlay */}
        <div className="relative aspect-[16/10] overflow-hidden">
          <img 
            src={playbook.image} 
            alt={playbook.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/40 to-transparent" />
          
          {/* Playbook Badge */}
          <div className="absolute top-3 left-3 flex items-center gap-2 px-2.5 py-1 bg-[#0A0A0A]/80 backdrop-blur-sm rounded-full border border-white/10">
            <div className="flex gap-0.5">
              {[1, 2, 3].map((i) => (
                <div key={i} className="w-0.5 rounded-full bg-[#D4AF37]" style={{ height: `${4 + i * 2}px` }} />
              ))}
            </div>
            <span className="text-[10px] text-zinc-300 font-medium uppercase tracking-wider">Playbook</span>
          </div>
          
          {/* Featured Badge */}
          {playbook.isFeatured && (
            <div className="absolute top-3 right-3 px-2.5 py-1 bg-[#D4AF37] rounded-full">
              <span className="text-[10px] font-bold text-[#0A0A0A] uppercase">Featured</span>
            </div>
          )}
          
          {/* Title on Image */}
          <div className="absolute bottom-0 left-0 right-0 p-4">
            <div className="flex items-center gap-2 mb-1.5">
              <div className="w-8 h-8 rounded-lg bg-[#D4AF37]/20 backdrop-blur-sm flex items-center justify-center">
                <Icon className="w-4 h-4 text-[#D4AF37]" />
              </div>
              <span className="text-xs text-[#D4AF37] font-medium">{playbook.subtitle}</span>
            </div>
            <h3 className="text-lg sm:text-xl font-bold text-white group-hover:text-[#D4AF37] transition-colors">
              {playbook.title}
            </h3>
          </div>
        </div>
        
        {/* Content */}
        <div className="p-4 sm:p-5">
          {/* Description */}
          <p className="text-sm text-zinc-300 mb-4 leading-relaxed">
            {playbook.desc}
          </p>
          
          {/* Takeaways */}
          <div className="space-y-2 mb-4">
            {playbook.takeaways.slice(0, isDesktop ? 3 : 2).map((takeaway) => (
              <div key={`takeaway-${takeaway.slice(0, 20)}`} className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                <span className="text-xs text-zinc-400 leading-relaxed">{takeaway}</span>
              </div>
            ))}
          </div>
          
          {/* CTAs */}
          <div className="flex gap-2">
            <button
              className="flex-1 py-2.5 sm:py-3 px-3 bg-[#D4AF37]/10 hover:bg-[#D4AF37]/20 border border-[#D4AF37]/30 rounded-lg text-[#D4AF37] font-medium text-sm flex items-center justify-center gap-2 transition-all"
            >
              <BookOpen className="w-4 h-4" />
              Preview
            </button>
            <a
              href={playbook.href}
              onClick={(e) => e.stopPropagation()}
              className="flex-1 py-2.5 sm:py-3 px-3 bg-[#D4AF37] hover:bg-[#E8C547] rounded-lg text-[#0A0A0A] font-medium text-sm flex items-center justify-center gap-2 transition-all"
            >
              Read Full
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

// Playbook Preview Modal
const PlaybookModal = ({ playbook, isOpen, onClose }) => {
  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen || !playbook) return null;

  const Icon = playbook.icon;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/85 backdrop-blur-sm animate-fadeIn" />
      
      {/* Modal Content */}
      <div 
        className="relative w-full sm:w-auto sm:max-w-2xl mx-0 sm:mx-4 bg-[#1C1C1C] rounded-t-3xl sm:rounded-2xl border border-white/10 max-h-[92vh] overflow-hidden animate-slideUp sm:animate-fadeIn"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Hero Image */}
        <div className="relative aspect-[16/9] sm:aspect-[2/1] overflow-hidden">
          <img 
            src={playbook.modalImage || playbook.image} 
            alt={playbook.title}
            className="w-full h-full object-cover"
          />
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#1C1C1C] via-[#1C1C1C]/60 to-transparent" />
          
          {/* Close Button */}
          <button 
            onClick={onClose}
            className="absolute top-3 right-3 w-10 h-10 rounded-full bg-black/50 backdrop-blur-sm hover:bg-black/70 flex items-center justify-center text-white transition-colors border border-white/10"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
          
          {/* Title on Image */}
          <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/20 backdrop-blur-sm flex items-center justify-center">
                <Icon className="w-5 h-5 text-[#D4AF37]" />
              </div>
              <div>
                <span className="text-xs text-[#D4AF37] font-medium block">{playbook.subtitle}</span>
                <h3 className="text-xl sm:text-2xl font-bold text-white">{playbook.title}</h3>
              </div>
            </div>
          </div>
        </div>
        
        {/* Body - Scrollable */}
        <div className="overflow-y-auto max-h-[45vh] sm:max-h-[50vh]">
          <div className="p-5 sm:p-6">
            {/* Stats Row */}
            <div className="flex gap-6 mb-6 pb-6 border-b border-white/10">
              <div>
                <div className="text-2xl font-bold text-[#D4AF37]">{playbook.preview.stats.pages}</div>
                <div className="text-xs text-zinc-500 uppercase">Pages</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-white">{playbook.preview.stats.readTime}</div>
                <div className="text-xs text-zinc-500 uppercase">Read Time</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-white">{playbook.preview.stats.frameworks}</div>
                <div className="text-xs text-zinc-500 uppercase">Frameworks</div>
              </div>
            </div>
            
            {/* Description */}
            <p className="text-base text-zinc-200 mb-6 leading-relaxed">
              {playbook.desc}
            </p>
            
            {/* What You'll Learn */}
            <h4 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-[#D4AF37]" />
              What You'll Learn
            </h4>
            <div className="space-y-3 mb-6">
              {playbook.takeaways.map((takeaway, i) => (
                <div key={`modal-takeaway-${takeaway.slice(0, 20)}`} className="flex items-start gap-3 p-3 bg-[#0A0A0A] rounded-xl border border-white/5">
                  <div className="w-6 h-6 rounded-full bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                    <span className="text-xs font-bold text-[#D4AF37]">{i + 1}</span>
                  </div>
                  <span className="text-sm text-zinc-300">{takeaway}</span>
                </div>
              ))}
            </div>
            
            {/* Chapters Preview */}
            <h4 className="text-sm font-semibold text-white mb-4 flex items-center gap-2">
              <BookOpen className="w-4 h-4 text-[#D4AF37]" />
              Inside This Playbook
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {playbook.preview.chapters.map((chapter) => (
                <div key={chapter} className="flex items-center gap-2.5 p-2.5 bg-[#0A0A0A] rounded-lg border border-white/5">
                  <div className="w-5 h-5 rounded bg-white/5 flex items-center justify-center flex-shrink-0">
                    <span className="text-[10px] text-zinc-500 font-medium">{playbook.preview.chapters.indexOf(chapter) + 1}</span>
                  </div>
                  <span className="text-xs text-zinc-400">{chapter}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        {/* Footer CTA */}
        <div className="p-4 sm:p-5 border-t border-white/10 bg-[#0A0A0A]/50">
          <a
            href={playbook.href}
            className="w-full py-3.5 px-6 bg-[#D4AF37] hover:bg-[#E8C547] text-[#0A0A0A] rounded-xl font-semibold text-sm flex items-center justify-center gap-2 transition-all shadow-lg shadow-[#D4AF37]/20"
          >
            Explore Full Playbook
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </div>
  );
};

// Main Section Component
export const StartHereSection = () => {
  const [selectedPlaybook, setSelectedPlaybook] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const scrollRef = useRef(null);

  const openPreview = (playbook) => {
    setSelectedPlaybook(playbook);
    setModalOpen(true);
  };

  const closePreview = () => {
    setModalOpen(false);
  };

  const scroll = (direction) => {
    if (scrollRef.current) {
      const scrollAmount = direction === 'left' ? -300 : 300;
      scrollRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  return (
    <section id="playbooks" className="py-12 sm:py-16 lg:py-20 bg-[#0A0A0A]">
      {/* Header */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="text-center mb-8 sm:mb-10">
          <div className="text-xs sm:text-sm font-mono text-[#D4AF37] tracking-widest uppercase mb-2 sm:mb-3">
            Playbooks, Tools & Frameworks
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold text-white tracking-tight mb-2 sm:mb-3">
            Things I've Built
          </h2>
          <p className="text-sm sm:text-base text-zinc-300 max-w-xl mx-auto">
            Playbooks, calculators, and frameworks drawn from years of building and running SDR organizations.
          </p>
        </div>
      </div>

      {/* Mobile Carousel */}
      <div className="lg:hidden relative">
        {/* Scrollable Container */}
        <div 
          ref={scrollRef}
          className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide pb-4 px-4 -mx-2"
          style={{ 
            scrollbarWidth: 'none', 
            msOverflowStyle: 'none',
            WebkitOverflowScrolling: 'touch'
          }}
        >
          {playbooks.map((playbook) => (
            <PlaybookCard 
              key={playbook.id} 
              playbook={playbook}
              onPreview={openPreview}
            />
          ))}
        </div>
        
        {/* Scroll Indicator */}
        <div className="flex justify-center gap-2 mt-2">
          {playbooks.map((playbook) => (
            <div 
              key={playbook.id}
              className="w-2 h-2 rounded-full bg-white/20"
            />
          ))}
        </div>
      </div>

      {/* Desktop - Horizontal Scroll with Navigation */}
      <div className="hidden lg:block max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 relative">
        {/* Scroll Buttons */}
        <button
          onClick={() => scroll('left')}
          className="absolute left-2 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-[#1C1C1C] border border-white/20 hover:border-[#D4AF37]/50 flex items-center justify-center text-white hover:text-[#D4AF37] transition-all shadow-lg"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <button
          onClick={() => scroll('right')}
          className="absolute right-2 top-1/2 -translate-y-1/2 z-10 w-10 h-10 rounded-full bg-[#1C1C1C] border border-white/20 hover:border-[#D4AF37]/50 flex items-center justify-center text-white hover:text-[#D4AF37] transition-all shadow-lg"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
        
        {/* Scrollable Grid */}
        <div 
          ref={scrollRef}
          className="flex gap-6 overflow-x-auto scrollbar-hide pb-4 px-8"
          style={{ 
            scrollbarWidth: 'none', 
            msOverflowStyle: 'none',
            scrollBehavior: 'smooth'
          }}
        >
          {playbooks.map((playbook) => (
            <div key={playbook.id} className="flex-shrink-0 w-[340px]">
              <PlaybookCard 
                playbook={playbook}
                onPreview={openPreview}
                isDesktop={true}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Preview Modal */}
      <PlaybookModal
        playbook={selectedPlaybook}
        isOpen={modalOpen}
        onClose={closePreview}
      />
    </section>
  );
};

export default StartHereSection;
